<template>
  <section class="page-shell">
    <div class="page-heading">
      <div>
        <h1>Evidências</h1>
        <p>Dossiês, registros e documentos gerados para auditoria.</p>
      </div>
      <v-btn color="primary" @click="openDialog">
        <v-icon left small>mdi-plus</v-icon>
        Anexar evidência externa
      </v-btn>
    </div>

    <v-alert v-if="error" type="error" dense text>{{ error }}</v-alert>

    <section class="data-panel">
      <v-simple-table>
        <thead>
          <tr>
            <th>Nome</th>
            <th>Origem</th>
            <th>Referência</th>
            <th>Data</th>
            <th>Ações</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="evidence in evidences" :key="evidence.id">
            <td><strong>{{ evidence.name }}</strong></td>
            <td>
              <strong>{{ originLabel(evidence) }}</strong>
              <span v-if="technicalReference(evidence)" class="technical-reference">{{ technicalReference(evidence) }}</span>
            </td>
            <td>{{ fileReference(evidence) }}</td>
            <td>{{ formatDate(evidence.created_at) }}</td>
            <td>
              <v-btn small outlined color="primary" :loading="detailLoadingId === evidence.id" @click="openDetails(evidence)">
                Ver detalhes
              </v-btn>
            </td>
          </tr>
          <tr v-if="!loading && !evidences.length">
            <td colspan="5" class="empty-cell">Nenhuma evidência registrada ainda.</td>
          </tr>
        </tbody>
      </v-simple-table>
    </section>

    <v-dialog v-model="dialog" max-width="560">
      <v-card>
        <v-card-title>Anexar evidência externa</v-card-title>
        <v-card-text>
          <v-text-field v-model="form.name" label="Nome da evidência" outlined dense />
          <v-select v-model="form.source" :items="manualSources" label="Origem do documento" outlined dense />
          <v-text-field v-model="form.storage_path" label="Referência, link ou localização do arquivo" outlined dense />
          <p class="dialog-helper">
            Evidências geradas dentro do ComplyOps já recebem vínculo automático com validações, solicitações, ferramentas e auditorias.
            Use este formulário apenas para documentos externos ao sistema.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialog = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="saving" @click="save">Salvar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="detailsDialog" max-width="980">
      <v-card v-if="selectedEvidence" class="evidence-detail-dialog">
        <v-card-title>
          <div>
            <span>Evidência</span>
            <strong>{{ selectedEvidence.name }}</strong>
          </div>
          <v-spacer />
          <v-btn icon @click="detailsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-alert v-if="!validationDetail" type="info" dense text>
            Esta evidência não possui detalhamento de validação salvo. Novas validações passam a registrar decisão, checklist, análise automatizada e referências jurídicas.
          </v-alert>

          <template v-else>
            <section class="evidence-detail-summary">
              <article>
                <span>Decisão</span>
                <strong>{{ validationDetail.decision.label }}</strong>
                <p>{{ validationDetail.decision.message }}</p>
              </article>
              <article>
                <span>Risco</span>
                <strong>{{ riskLabel(validationDetail.decision.risk_level) }}</strong>
                <p>{{ validationDetail.decision.next_step }}</p>
              </article>
              <article>
                <span>Documento</span>
                <strong>{{ documentLabel(validationDetail.source_document) }}</strong>
                <p>{{ validationDetail.source_document.word_count || 0 }} palavras</p>
              </article>
            </section>

            <section v-if="aiReview" class="evidence-detail-section">
              <div class="panel-title">
                <h2>Análise automatizada</h2>
              </div>
              <p>{{ aiReview.executive_summary || selectedEvidence.validation_detail.ai_review.summary }}</p>

              <div v-if="aiReview.legal_source_review" class="evidence-legal-source">
                <strong>Referências jurídicas</strong>
                <v-chip small :color="legalSourceStatus(aiReview.legal_source_review.status).color" text-color="white">
                  {{ legalSourceStatus(aiReview.legal_source_review.status).label }}
                </v-chip>
                <p>{{ aiReview.legal_source_review.summary }}</p>
                <ul v-if="aiReview.legal_source_review.concrete_references && aiReview.legal_source_review.concrete_references.length">
                  <li v-for="source in aiReview.legal_source_review.concrete_references" :key="`source-${source}`">{{ source }}</li>
                </ul>
                <ul v-if="aiReview.legal_source_review.incomplete_references && aiReview.legal_source_review.incomplete_references.length">
                  <li v-for="source in aiReview.legal_source_review.incomplete_references" :key="`missing-${source}`">{{ source }}</li>
                </ul>
              </div>

              <div class="evidence-detail-columns">
                <article>
                  <strong>Riscos</strong>
                  <ul>
                    <li v-for="risk in aiReview.main_risks || []" :key="risk">{{ risk }}</li>
                  </ul>
                </article>
                <article>
                  <strong>Ações exigidas</strong>
                  <ul>
                    <li v-for="action in aiReview.required_actions || []" :key="action">{{ action }}</li>
                  </ul>
                </article>
              </div>
            </section>

            <section class="evidence-detail-section">
              <div class="panel-title">
                <h2>Documento utilizado</h2>
                <span>{{ documentPreviewMeta }}</span>
              </div>
              <div v-if="documentPreviewText" class="evidence-document-preview">
                <pre>{{ documentPreviewText }}</pre>
              </div>
              <v-alert v-else type="info" dense text>
                Esta evidência foi criada antes do armazenamento do texto usado na validação. Novas validações passam a exibir o documento ou texto utilizado aqui.
              </v-alert>
            </section>

            <section class="evidence-detail-section">
              <div class="panel-title">
                <h2>Checklist de validação</h2>
                <span>{{ validationDetail.checks.length }} verificações</span>
              </div>
              <div class="evidence-check-list">
                <article v-for="check in validationDetail.checks" :key="check.name" :class="`check-${check.status}`">
                  <v-icon>{{ check.status === 'passed' ? 'mdi-check-circle-outline' : 'mdi-alert-circle-outline' }}</v-icon>
                  <div>
                    <strong>{{ check.name }}</strong>
                    <p>{{ check.message }}</p>
                  </div>
                </article>
              </div>
            </section>

            <section class="evidence-detail-section">
              <div class="panel-title">
                <h2>Dados operacionais</h2>
                <span>{{ formatDate(selectedEvidence.created_at) }}</span>
              </div>
              <div class="evidence-facts-grid">
                <article>
                  <span>Tipo</span>
                  <strong>{{ validationDetail.facts.document_type || '-' }}</strong>
                </article>
                <article>
                  <span>Uso pretendido</span>
                  <strong>{{ validationDetail.facts.intended_use || '-' }}</strong>
                </article>
                <article>
                  <span>Cliente</span>
                  <strong>{{ validationDetail.facts.client_name || '-' }}</strong>
                </article>
                <article>
                  <span>Arquivo</span>
                  <strong>{{ validationDetail.source_document.file_name || 'Texto colado' }}</strong>
                </article>
              </div>
            </section>
          </template>
        </v-card-text>
      </v-card>
    </v-dialog>
  </section>
</template>

<script>
import api from '../../plugins/axios'
import { billingErrorMessage } from '../../utils/apiErrors'

export default {
  name: 'EvidencePage',
  data () {
    return {
      evidences: [],
      dialog: false,
      loading: false,
      saving: false,
      detailLoadingId: null,
      error: '',
      detailsDialog: false,
      selectedEvidence: null,
      manualSources: [
        { text: 'E-mail de aprovação', value: 'approval_email' },
        { text: 'Contrato ou aditivo', value: 'contract' },
        { text: 'Ata ou reunião', value: 'meeting_record' },
        { text: 'Parecer jurídico externo', value: 'external_legal_opinion' },
        { text: 'Treinamento ou política assinada', value: 'training_or_policy' },
        { text: 'Outro documento externo', value: 'external_document' }
      ],
      form: {
        name: '',
        source: 'external_document',
        storage_path: ''
      }
    }
  },
  computed: {
    validationDetail () {
      return this.selectedEvidence ? this.selectedEvidence.validation_detail : null
    },
    aiReview () {
      if (!this.validationDetail || !this.validationDetail.ai_review) return null
      return this.validationDetail.ai_review.review || null
    },
    documentPreviewText () {
      if (!this.validationDetail || !this.validationDetail.source_document) return ''
      return this.validationDetail.source_document.text_preview || this.validationDetail.source_document.text || ''
    },
    documentPreviewMeta () {
      if (!this.validationDetail || !this.validationDetail.source_document) return ''
      const source = this.validationDetail.source_document
      const words = source.word_count ? `${source.word_count} palavras` : 'sem contagem'
      return source.text_truncated ? `${words} · texto parcial` : words
    }
  },
  created () {
    this.load()
  },
  methods: {
    async load () {
      this.loading = true
      try {
        const { data } = await api.get('/evidences')
        this.evidences = data
      } catch (error) {
        this.error = billingErrorMessage(error, 'Não foi possível carregar evidências.')
      } finally {
        this.loading = false
      }
    },
    async save () {
      this.saving = true
      try {
        await api.post('/evidences', {
          entity_type: 'manual_evidence',
          entity_id: 0,
          name: this.form.name,
          storage_path: this.form.storage_path,
          source: this.form.source
        })
        this.dialog = false
        this.form = this.emptyForm()
        await this.load()
      } catch (error) {
        this.error = error.response && error.response.data ? error.response.data.message : 'Não foi possível registrar evidência.'
      } finally {
        this.saving = false
      }
    },
    formatDate (value) {
      return value ? new Date(value).toLocaleString('pt-BR') : '-'
    },
    openDialog () {
      this.form = this.emptyForm()
      this.dialog = true
    },
    async openDetails (evidence) {
      this.detailLoadingId = evidence.id
      this.error = ''
      try {
        const { data } = await api.get(`/evidences/${evidence.id}`)
        this.selectedEvidence = data
        this.detailsDialog = true
      } catch (error) {
        this.error = error.response && error.response.data ? error.response.data.message : 'Não foi possível carregar os detalhes da evidência.'
      } finally {
        this.detailLoadingId = null
      }
    },
    emptyForm () {
      return { name: '', source: 'external_document', storage_path: '' }
    },
    riskLabel (risk) {
      return { low: 'Baixo', medium: 'Médio', high: 'Alto', critical: 'Crítico' }[risk] || risk || '-'
    },
    documentLabel (source) {
      if (!source) return '-'
      return source.file_name || source.file_type || 'Texto'
    },
    legalSourceStatus (status) {
      return {
        verified_by_text: { label: 'Referências concretas', color: 'success' },
        needs_official_check: { label: 'Conferir fonte oficial', color: 'warning' },
        incomplete_or_placeholder: { label: 'Incompleto', color: 'error' },
        no_sources_found: { label: 'Sem referências', color: 'grey' }
      }[status] || { label: 'Conferir', color: 'warning' }
    },
    originLabel (evidence) {
      const labels = {
        ai_use_request: 'Governança de uso de IA',
        human_document_review: 'Revisão documental',
        safe_ai_use_check: 'Verificação operacional',
        ai_tool: 'Regra operacional',
        ai_document_validation: 'Validação de documento',
        governance_policy: 'Política de validação',
        manual_evidence: 'Documento externo'
      }

      return labels[evidence.entity_type] || 'Registro automático'
    },
    technicalReference (evidence) {
      if (!evidence.entity_id || Number(evidence.entity_id) === 0 || evidence.entity_type === 'manual_evidence') {
        return ''
      }

      return `Referência automática #${evidence.entity_id}`
    },
    fileReference (evidence) {
      if (!evidence.storage_path) {
        return '-'
      }

      if (evidence.storage_path.indexOf('manual://') === 0) {
        return this.manualSourceLabel(evidence.storage_path.split('/')[2])
      }

      if (evidence.storage_path.indexOf('generated://') === 0) {
        return 'Gerada automaticamente pelo ComplyOps'
      }

      return evidence.storage_path
    },
    manualSourceLabel (source) {
      const labels = {
        approval_email: 'E-mail de aprovação',
        contract: 'Contrato ou aditivo',
        meeting_record: 'Ata ou reunião',
        external_legal_opinion: 'Parecer jurídico externo',
        training_or_policy: 'Treinamento ou política assinada',
        external_document: 'Documento externo'
      }

      return labels[source] || 'Documento externo'
    }
  }
}
</script>

<style scoped>
.technical-reference {
  display: block;
  margin-top: 2px;
  color: #607a75;
  font-size: 12px;
  font-weight: 500;
}

.dialog-helper {
  color: #607a75;
  font-size: 13px;
  line-height: 1.5;
  margin: 4px 0 0;
}

.evidence-detail-dialog .v-card__title {
  align-items: flex-start;
  border-bottom: 1px solid #e6ecea;
}

.evidence-detail-dialog .v-card__title span {
  display: block;
  color: #0f4efb;
  font-size: 11px;
  font-weight: 850;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.evidence-detail-dialog .v-card__title strong {
  display: block;
  margin-top: 4px;
  color: #111111;
  font-size: 22px;
}

.evidence-detail-summary,
.evidence-detail-columns,
.evidence-facts-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.evidence-detail-summary article,
.evidence-detail-columns article,
.evidence-facts-grid article,
.evidence-legal-source {
  padding: 14px;
  border: 1px solid #e0e9e6;
  border-radius: 8px;
  background: #ffffff;
}

.evidence-detail-summary span,
.evidence-facts-grid span {
  display: block;
  color: #667a75;
  font-size: 11px;
  font-weight: 850;
  letter-spacing: .06em;
  text-transform: uppercase;
}

.evidence-detail-summary strong,
.evidence-facts-grid strong,
.evidence-detail-columns strong,
.evidence-legal-source strong {
  display: block;
  color: #111111;
  font-size: 14px;
}

.evidence-detail-summary p,
.evidence-detail-section p,
.evidence-facts-grid p,
.evidence-legal-source p {
  margin: 6px 0 0;
  color: #526660;
  font-size: 13px;
  line-height: 1.45;
}

.evidence-detail-section {
  margin-top: 18px;
}

.evidence-detail-section .panel-title {
  margin-bottom: 10px;
}

.evidence-detail-columns {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-top: 12px;
}

.evidence-detail-columns ul,
.evidence-legal-source ul {
  margin: 8px 0 0;
  padding-left: 18px;
  color: #526660;
  font-size: 13px;
  line-height: 1.45;
}

.evidence-legal-source {
  margin-top: 12px;
}

.evidence-legal-source .v-chip {
  margin-top: 8px;
}

.evidence-document-preview {
  max-height: 360px;
  overflow: auto;
  padding: 18px;
  border: 1px solid #e0e9e6;
  border-radius: 8px;
  background: #fbfcfc;
}

.evidence-document-preview pre {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  color: #182522;
  font-family: Georgia, "Times New Roman", serif;
  font-size: 14px;
  line-height: 1.65;
}

.evidence-check-list {
  display: grid;
  gap: 10px;
}

.evidence-check-list article {
  display: grid;
  grid-template-columns: 28px 1fr;
  gap: 10px;
  padding: 12px;
  border: 1px solid #e0e9e6;
  border-radius: 8px;
  background: #ffffff;
}

.evidence-check-list article.check-failed {
  border-color: #f1d7d7;
  background: #fff8f8;
}

.evidence-check-list strong {
  color: #111111;
  font-size: 13px;
}

.evidence-check-list p {
  margin: 3px 0 0;
  color: #526660;
  font-size: 13px;
}

.evidence-facts-grid {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

@media (max-width: 960px) {
  .evidence-detail-summary,
  .evidence-detail-columns,
  .evidence-facts-grid {
    grid-template-columns: 1fr;
  }
}
</style>
